import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

// Default region used when the user types a number without a country code
// (e.g. "650-922-0999" is assumed to be US).
const DEFAULT_COUNTRY: CountryCode = 'US';

/**
 * Returns true if `input` is a valid phone number. Accepts friendly formats
 * (650-922-0999, (650) 922-0999, 6509220999) as well as full E.164 (+16509220999).
 * Numbers without a country code are validated against `defaultCountry`.
 */
export const isValidPhone = (
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY
): boolean => {
  if (!input) {
    return false;
  }
  return isValidPhoneNumber(input.trim(), defaultCountry);
};

/**
 * Normalizes `input` to E.164 (the format Twilio requires, e.g. +16509220999).
 * Returns null if the number can't be parsed into a valid phone number.
 */
export const toE164 = (
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY
): string | null => {
  if (!input) {
    return null;
  }
  const parsed = parsePhoneNumberFromString(input.trim(), defaultCountry);
  if (!parsed || !parsed.isValid()) {
    return null;
  }
  return parsed.format('E.164');
};

/**
 * Progressively masks `input` as the user types, using libphonenumber's AsYouType so the
 * mask adapts to the detected country (e.g. "5550123456" -> "(555) 012-3456",
 * "+15550123456" -> "+1 555 012 3456", pasted "15550123456" -> "1 (555) 012-3456").
 *
 * The length limit is enforced by libphonenumber itself: any trailing digits that make the
 * number too long for its (detected or default) country are dropped, so a US number can't
 * exceed 10 national digits whether or not a "+1" country code is present. When the last
 * character is a separator the formatter just inserted, we strip it so backspacing over
 * separators isn't blocked.
 */
export const formatPhoneAsYouType = (
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY
): string => {
  if (!input) {
    return '';
  }
  const trimmed = input.trim();
  const hasPlus = trimmed.startsWith('+');
  // Hard ceiling at the E.164 max (15 digits) before we ask libphonenumber for the exact
  // valid length for this number's country.
  let digits = trimmed.replace(/\D/g, '').slice(0, 15);
  // Drop any trailing digits that push the number past a valid length for its country.
  while (digits.length > 1) {
    const candidate = hasPlus ? `+${digits}` : digits;
    const lengthError = validatePhoneNumberLength(
      candidate,
      hasPlus ? undefined : { defaultCountry }
    );
    if (lengthError === 'TOO_LONG') {
      digits = digits.slice(0, -1);
    } else {
      break;
    }
  }
  const capped = hasPlus ? `+${digits}` : digits;
  const formatted = new AsYouType(defaultCountry).input(capped);
  if (/[-\s()]$/.test(formatted) && !/[-\s()]$/.test(capped)) {
    return formatted.slice(0, -1);
  }
  return formatted;
};

/**
 * Formats a stored (E.164) number for display: national format for US numbers
 * ("(434) 534-5345"), international otherwise ("+44 20 7183 8750"). Falls back to the input
 * if it can't be parsed.
 */
export const formatPhoneForDisplay = (
  input: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY
): string => {
  if (!input) {
    return '';
  }
  const parsed = parsePhoneNumberFromString(input.trim(), defaultCountry);
  if (!parsed) {
    return input;
  }
  return parsed.country === defaultCountry ? parsed.formatNational() : parsed.formatInternational();
};
